import crudApi from "@serie3/common/api/crudApi";
import { useState, useEffect } from "react";
import ProjectConfig from "@serie3/freelancesplaceapi/Config/Projects/ProjectConfig";
import NewModal from "../../../../documents/modals/NewModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faGaugeHigh,
  faCircleMinus,
  faShareNodes,
  faKey,
  faLink,
  faTriangleExclamation,
  faPalette,
  faFloppyDisk,
  faImage,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { btn, btnHoverStyle } from "../../../../../../layout/Buttons/buttonStyles";

const randomLightColor = () => {
  const h = Math.floor(Math.random() * 360);
  const s = Math.floor(Math.random() * 30) + 40; // 40–70%
  const l = Math.floor(Math.random() * 15) + 70; // 70–85% — light
  const hslToHex = (h, s, l) => {
    s /= 100; l /= 100;
    const k = (n) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return "#" + [f(0), f(8), f(4)].map((x) => Math.round(x * 255).toString(16).padStart(2, "0")).join("");
  };
  return hslToHex(h, s, l);
};

const SettingCard = ({ icon, title, description, children }) => (
  <div style={{
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    marginBottom: "16px",
    overflow: "hidden",
  }}>
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "16px 20px",
      borderBottom: "1px solid #f0f0f0",
    }}>
      <div style={{
        width: "36px",
        height: "36px",
        borderRadius: "10px",
        background: "#e8f0fe",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#1a73e8",
        fontSize: "0.9rem",
        flexShrink: 0,
      }}>
        <FontAwesomeIcon icon={icon} />
      </div>
      <div>
        <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#212121" }}>{title}</div>
        {description && (
          <div style={{ fontSize: "0.8rem", color: "#757575", marginTop: "2px" }}>{description}</div>
        )}
      </div>
    </div>
    <div style={{ padding: "16px 20px" }}>{children}</div>
  </div>
);

const ProjectSettings = ({
  formik,
  project,
  projectId,
  publicId,
  setPublicId,
  submitError,
}) => {
  const [showPassword, setShowPassword] = useState();
  const [password, setPassword] = useState();
  const [hasQuicklink, setHasQuicklink] = useState(!!project.quicklinklist);
  const [isInDashboard, setShowIsInDashboard] = useState(!!project.show_in_dashboard);

  const [localColor, setLocalColor] = useState(formik.values.color || "#a5c8f0");
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (project.file_name) {
      crudApi.getBinary("/project/filePreview/" + project.id).then((response) => {
        if (response?.data) setLogoPreview(URL.createObjectURL(response.data));
      });
    }
  }, [project.id]);

  const buildPayload = (overrides = {}) => {
    const values = { ...formik.values, ...overrides };
    values.finance = (values.finance || []).map((f) => ({ id: f.id }));
    values.documents = (values.documents || []).map((d) => ({ id: d.id }));
    return JSON.stringify(values);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      setLogoPreview(reader.result);
      await crudApi.edit(ProjectConfig.endpoints.edit, buildPayload({ file_name: file.name, file_string: reader.result }));
      formik.setFieldValue("file_name", file.name);
      formik.setFieldValue("file_string", reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    setLogoPreview(null);
    await crudApi.edit(ProjectConfig.endpoints.edit, buildPayload({ file_name: "", file_string: "" }));
    formik.setFieldValue("file_name", "");
    formik.setFieldValue("file_string", "");
  };

  const applyColor = async (hex) => {
    const color = hex || randomLightColor();
    setLocalColor(color);
    formik.setFieldValue("color", color);
    await formik.submitForm();
  };

  const shareLink =
    window.location.protocol + "//" + window.location.host + "/publicdashboard/" + publicId;

  const addToQuickLinkList = async () => {
    formik.setFieldValue("quicklinklist", true);
    await formik.handleSubmit();
    if (submitError === false) setHasQuicklink(true);
  };

  const removeFromQuickLinkList = async () => {
    formik.setFieldValue("quicklinklist", false);
    await formik.handleSubmit();
    if (submitError === false) setHasQuicklink(false);
  };

  const addToDashboard = async () => {
    formik.setFieldValue("show_in_dashboard", true);
    await formik.handleSubmit();
    if (submitError === false) setShowIsInDashboard(true);
  };

  const removeFromDashboard = async () => {
    formik.setFieldValue("show_in_dashboard", false);
    await formik.handleSubmit();
    if (submitError === false) setShowIsInDashboard(false);
  };

  const setLink = async () => {
    const response = await crudApi.justGet(ProjectConfig.endpoints.share + projectId);
    if (response.status === 200) {
      setPublicId(response.data.uniqueId);
      setPassword(response.data.password);
      setShowPassword(true);
    }
  };

  return (
    <>
      <style>{btnHoverStyle}</style>

      <NewModal setShow={setShowPassword} show={showPassword} title="Share Password">
        <div style={{ fontSize: "0.95rem", color: "#424242", lineHeight: 1.6 }}>
          Send this password to your customer via a <strong>different channel</strong> than the link.
          You will never see this password again, but you can generate a new one at any time.
        </div>
        <div style={{
          marginTop: "16px",
          background: "#f8f9fa",
          borderRadius: "10px",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          border: "1px solid #e0e0e0",
        }}>
          <FontAwesomeIcon icon={faKey} style={{ color: "#1a73e8", fontSize: "1.1rem" }} />
          <span style={{ fontWeight: 700, fontSize: "1.1rem", color: "#212121", letterSpacing: "0.06em" }}>
            {password}
          </span>
        </div>
      </NewModal>

      <div style={{ maxWidth: "600px" }}>

        <SettingCard
          icon={faStar}
          title="Quicklinks"
          description="Pin this project to your quicklinks for fast access"
        >
          {hasQuicklink === false ? (
            <button
              className="btn-modern"
              style={{ ...btn.base, ...btn.primary }}
              onClick={addToQuickLinkList}
            >
              <FontAwesomeIcon icon={faStar} /> Add to Quicklinks
            </button>
          ) : (
            <button
              className="btn-modern"
              style={{ ...btn.base, ...btn.danger }}
              onClick={removeFromQuickLinkList}
            >
              <FontAwesomeIcon icon={faCircleMinus} /> Remove from Quicklinks
            </button>
          )}
        </SettingCard>

        <SettingCard
          icon={faGaugeHigh}
          title="Dashboard"
          description="Show this project's stats on your main dashboard"
        >
          {isInDashboard === false ? (
            <button
              className="btn-modern"
              style={{ ...btn.base, ...btn.primary }}
              onClick={addToDashboard}
            >
              <FontAwesomeIcon icon={faGaugeHigh} /> Add to Dashboard
            </button>
          ) : (
            <button
              className="btn-modern"
              style={{ ...btn.base, ...btn.danger }}
              onClick={removeFromDashboard}
            >
              <FontAwesomeIcon icon={faCircleMinus} /> Remove from Dashboard
            </button>
          )}
        </SettingCard>

        <SettingCard
          icon={faPalette}
          title="Project Color"
          description="Used for the project icon and header accent to distinguish projects at a glance"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <input
                type="color"
                value={localColor}
                onChange={(e) => setLocalColor(e.target.value)}
                onBlur={(e) => applyColor(e.target.value)}
                style={{
                  width: "48px", height: "48px",
                  border: "2px solid #e0e0e0", borderRadius: "10px",
                  padding: "2px", cursor: "pointer", background: "none",
                }}
              />
              {localColor && (
                <div style={{
                  width: "80px", height: "48px",
                  borderRadius: "10px", background: localColor,
                  border: "1px solid rgba(0,0,0,0.08)",
                }} />
              )}
              <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#424242" }}>
                {localColor || <span style={{ color: "#9e9e9e" }}>No color set</span>}
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                className="btn-modern"
                style={{ ...btn.base, ...btn.secondary, fontSize: "0.78rem", padding: "4px 10px" }}
                onClick={() => applyColor(null)}
              >
                <FontAwesomeIcon icon={faPalette} /> Random color
              </button>
              <button
                type="button"
                className="btn-modern"
                style={{ ...btn.base, ...btn.primary, fontSize: "0.78rem", padding: "4px 10px" }}
                onClick={() => applyColor(localColor)}
              >
                <FontAwesomeIcon icon={faFloppyDisk} /> Save color
              </button>
            </div>
          </div>
        </SettingCard>

        <SettingCard
          icon={faImage}
          title="Project Logo"
          description="Upload a logo to identify this project visually"
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Project logo"
                style={{ width: "72px", height: "72px", objectFit: "contain", borderRadius: "10px", border: "1px solid #e0e0e0", background: "#fafafa" }}
              />
            ) : (
              <div style={{ width: "72px", height: "72px", borderRadius: "10px", border: "2px dashed #e0e0e0", display: "flex", alignItems: "center", justifyContent: "center", color: "#bdbdbd" }}>
                <FontAwesomeIcon icon={faImage} style={{ fontSize: "1.6rem" }} />
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label
                style={{ ...btn.base, ...btn.primary, fontSize: "0.78rem", padding: "5px 12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }}
              >
                <FontAwesomeIcon icon={faImage} /> {logoPreview ? "Change Logo" : "Upload Logo"}
                <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />
              </label>
              {logoPreview && (
                <button
                  type="button"
                  className="btn-modern"
                  style={{ ...btn.base, ...btn.danger, fontSize: "0.78rem", padding: "5px 12px" }}
                  onClick={removeLogo}
                >
                  <FontAwesomeIcon icon={faTrash} /> Remove
                </button>
              )}
            </div>
          </div>
        </SettingCard>

        <SettingCard
          icon={faShareNodes}
          title="Customer Share Link"
          description="Share a read-only public view of this project with your customer"
        >
          {publicId ? (
            <>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: "#f8f9fa",
                borderRadius: "8px",
                padding: "10px 14px",
                border: "1px solid #e0e0e0",
                marginBottom: "12px",
                overflow: "hidden",
              }}>
                <FontAwesomeIcon icon={faLink} style={{ color: "#1a73e8", flexShrink: 0, fontSize: "0.8rem" }} />
                <a
                  href={shareLink}
                  style={{
                    fontSize: "0.82rem",
                    color: "#1a73e8",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {shareLink}
                </a>
              </div>
              <button
                className="btn-modern"
                style={{ ...btn.base, ...btn.secondary }}
                onClick={setLink}
              >
                <FontAwesomeIcon icon={faKey} /> Change Password
              </button>
            </>
          ) : (
            <button
              className="btn-modern"
              style={{ ...btn.base, ...btn.primary }}
              onClick={setLink}
            >
              <FontAwesomeIcon icon={faShareNodes} /> Share Project
            </button>
          )}

          <div style={{
            marginTop: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 14px",
            background: "#fff8e1",
            borderRadius: "8px",
            border: "1px solid #ffe082",
            fontSize: "0.8rem",
            color: "#795548",
          }}>
            <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#f57c00", flexShrink: 0 }} />
            This is not a secure connection. Do not share sensitive data.
          </div>
        </SettingCard>

      </div>
    </>
  );
};

export default ProjectSettings;
